import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, CheckCircle2, Phone } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Drawer,
  DrawerContent,
  EmptyState,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@buzzbee/ui';
import { useState } from 'react';
import { OutcomeBadge, ScoreBadge, Stat, UpgradePrompt } from '../components/common';
import { DepositBadge } from '../components/rebooking-deposits';
import { WaitlistCard } from '../components/waitlist';
import { apiUrl, errorMessage, formatDuration, formatWhen, get, patch, post, type UpgradeInfo } from '../lib/api';
import { Link, useRouter } from '../lib/router';
import { hasFeature, useWorkspace, type WorkspaceSummary } from '../lib/session';

export function DashboardPage() {
  const ws = useWorkspace();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => get('/api/dashboard') });
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const pct = d.minutes.included ? Math.min(100, Math.round((d.minutes.used / d.minutes.included) * 100)) : 0;
  return (
    <div className="space-y-8">
      <PageHeader title={`Good day, ${ws.name}`} description={`Your ${ws.brand.name} receptionist over the last ${d.periodDays} days.`} actions={<Button asChild variant="outline"><Link to="/test-call"><Phone /> Test call</Link></Button>} />
      <UsageBanner ws={ws} minutes={d.minutes} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Calls answered" value={d.calls} hint={`${d.afterHoursCalls} after hours`} />
        <Stat label="Bookings" value={d.bookings} />
        <Stat label="New messages" value={d.newMessages} hint={<Link className="underline" to="/inbox">Open inbox</Link>} />
        <Stat label="Needs a person" value={d.openEscalations} hint={<Link className="underline" to="/inbox?tab=escalations">Review</Link>} />
        <Card>
          <CardContent className="p-5">
            <p className="text-[13px] font-medium text-muted-foreground">Voice minutes ({d.minutes.month})</p>
            <p className="figures mt-1 text-[28px] font-semibold leading-tight">
              {d.minutes.used}
              <span className="text-base text-muted-foreground"> / {d.minutes.included ?? 'unlimited'}</span>
            </p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Minutes used">
              <div className={`h-full ${pct >= 100 ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
            </div>
            {d.minutes.overageMinutes ? <p className="mt-1 text-xs text-muted-foreground">{d.minutes.overageMinutes} extra minutes (${(d.minutes.overageCents / 100).toFixed(2)})</p> : null}
          </CardContent>
        </Card>
      </div>
      <ThisWeek />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent calls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {d.recent.length === 0 ? <p className="text-sm text-muted-foreground">No calls yet. Forward your number to start.</p> : null}
            {d.recent.map((c: any) => (
              <Link key={c.id} to={`/calls/${c.id}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted">
                <OutcomeBadge outcome={c.outcome} />
                <span className="flex-1 truncate text-sm">{c.summary ?? c.contactName ?? c.contactPhone ?? 'Caller'}</span>
                <span className="figures text-xs text-muted-foreground">{formatWhen(c.startedAt, ws.timezone)}</span>
              </Link>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Upcoming bookings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {d.upcoming.length === 0 ? <p className="text-sm text-muted-foreground">Nothing booked yet.</p> : null}
            {d.upcoming.map((a: any) => (
              <div key={a.id} className="flex items-center gap-3 px-2 py-2">
                <span className="figures w-40 text-sm">{formatWhen(a.startsAt, ws.timezone)}</span>
                <span className="flex-1 truncate text-sm">
                  {a.serviceName} <span className="text-muted-foreground">· {a.contactName ?? 'Customer'}</span>
                </span>
                {a.status === 'pending_confirmation' ? <Badge variant="warning">Needs confirmation</Badge> : null}
                <DepositBadge cents={a.depositCents} status={a.depositStatus} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** At 80% and 100% of this month's included voice minutes (a cap pause has its own banner in the shell). */
function UsageBanner({ ws, minutes }: { ws: WorkspaceSummary; minutes: { used: number; included: number | null; paused: boolean } }) {
  if (!minutes.included || minutes.paused) return null;
  const pct = Math.round((minutes.used / minutes.included) * 100);
  if (pct < 80) return null;
  const full = pct >= 100;
  const cta =
    ws.managedBy === 'hive' ? (
      ws.hive?.billingUrl ? (
        <a className="font-medium underline" href={ws.hive.billingUrl}>
          Upgrade in Hive
        </a>
      ) : (
        'Billing is managed in WRKRBZ Hive.'
      )
    ) : (
      <Link className="font-medium underline" to="/settings/billing">
        Upgrade for more minutes
      </Link>
    );
  return (
    <Alert variant="warning" title={full ? `You have used all ${minutes.included} included voice minutes this month` : `You have used ${pct}% of your voice minutes this month`}>
      {full ? 'Calls are still answered; extra minutes are billed per minute up to your overage cap.' : `${minutes.used} of ${minutes.included} included minutes so far.`} {cta}
    </Alert>
  );
}

type WeeklyValue = {
  productName: string;
  callsAnswered: number;
  afterHoursCalls: number;
  aiBookings: number;
  bookedValueCents: number;
  messagesTaken: number;
  escalations: number;
  minutes: { used: number; included: number | null };
  teasers: { feature: string; count: number; text: string; upgrade: UpgradeInfo }[];
};

/** The weekly value email's numbers (GET /api/dashboard/week), with what a higher plan would have done. */
function ThisWeek() {
  const q = useQuery({ queryKey: ['dashboard-week'], queryFn: () => get<WeeklyValue>('/api/dashboard/week') });
  const r = q.data;
  if (!r) return null;
  const $ = (c: number) => `$${Math.round(c / 100).toLocaleString('en-CA')}`;
  // One prompt per plan, listing what its features would have done this week.
  const byPlan = new Map<string, { upgrade: UpgradeInfo; lines: string[] }>();
  for (const t of r.teasers) {
    const g = byPlan.get(t.upgrade.plan) ?? { upgrade: t.upgrade, lines: [] };
    g.lines.push(t.text);
    byPlan.set(t.upgrade.plan, g);
  }
  const stats: [string, string | number][] = [
    ['Calls answered', r.callsAnswered],
    ['After hours', r.afterHoursCalls],
    ['Booked for you', r.aiBookings],
    ['Booking value', $(r.bookedValueCents)],
    ['Messages taken', r.messagesTaken],
    ['Hand-offs', r.escalations],
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>This week</CardTitle>
        <CardDescription>
          The last 7 days with {r.productName}. Owners get this by email every Monday morning.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {stats.map(([label, value]) => (
            <div key={label}>
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="figures text-2xl font-semibold">{value}</p>
            </div>
          ))}
        </div>
        {byPlan.size ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[...byPlan.values()].map((g) => (
              <UpgradePrompt
                key={g.upgrade.plan}
                upgrade={g.upgrade}
                compact
                title={`What ${g.upgrade.planName} would have done`}
                detail={
                  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    {g.lines.map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                }
              />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function CallsPage() {
  const ws = useWorkspace();
  const { navigate } = useRouter();
  const [outcome, setOutcome] = useState('');
  const review = hasFeature(ws, 'analytics');
  // "Needs review" is a quality filter (score below 60 or a negative flag), not an outcome.
  const filter = outcome === 'needs_review' ? '&review=needs' : outcome ? `&outcome=${outcome}` : '';
  const q = useQuery({ queryKey: ['calls', outcome], queryFn: () => get(`/api/calls?limit=100${filter}`) });
  return (
    <div className="space-y-6">
      <PageHeader
        title="Call log"
        description="Every call and chat your AI receptionist handled, with transcripts."
        actions={
          <Select
            aria-label="Filter by outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            options={[
              { value: '', label: 'All outcomes' },
              { value: 'booked', label: 'Booked' },
              { value: 'message', label: 'Message' },
              { value: 'escalated', label: 'Needs a person' },
              { value: 'transferred', label: 'Transferred' },
              { value: 'answered', label: 'Answered' },
              ...(review ? [{ value: 'needs_review', label: 'Needs review' }] : []),
            ]}
          />
        }
      />
      {q.isLoading ? <LoadingState /> : null}
      {q.data?.calls.length === 0 ? <EmptyState icon={<Phone />} title="No calls yet" description="Run a test call or forward your business number to your BuzzBee number." /> : null}
      <Card className="divide-y">
        {q.data?.calls.map((c: any) => (
          <button key={c.id} type="button" onClick={() => navigate(`/calls/${c.id}`)} className="flex w-full flex-wrap items-center gap-3 px-5 py-3 text-left hover:bg-muted/60">
            <OutcomeBadge outcome={c.outcome} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{c.contactName ?? c.contactPhone ?? (c.channel === 'web_chat' ? 'Website visitor' : 'Caller')}</span>
              <span className="block truncate text-xs text-muted-foreground">{c.summary}</span>
            </span>
            {c.channel !== 'voice' ? <Badge variant="outline">{c.channel === 'web_chat' ? 'Chat' : c.channel}</Badge> : null}
            {c.afterHours ? <Badge variant="info">After hours</Badge> : null}
            {typeof c.score === 'number' ? <ScoreBadge score={c.score} needsReview={c.needsReview} /> : null}
            <span className="figures w-14 text-right text-xs text-muted-foreground">{formatDuration(c.durationSeconds)}</span>
            <span className="figures w-40 text-right text-xs text-muted-foreground">{formatWhen(c.startedAt, ws.timezone)}</span>
          </button>
        ))}
      </Card>
    </div>
  );
}

export function CallDetail({ id }: { id: string }) {
  const ws = useWorkspace();
  const { navigate } = useRouter();
  const q = useQuery({ queryKey: ['call', id], queryFn: () => get(`/api/calls/${id}`) });
  return (
    <Drawer open onOpenChange={(o) => !o && navigate('/calls')}>
      <DrawerContent side="right" width="lg" title="Call details">
        {q.isLoading ? <LoadingState /> : null}
        {q.error ? <ErrorState error={q.error} /> : null}
        {q.data ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <OutcomeBadge outcome={q.data.conversation.outcome} />
              <span className="text-sm text-muted-foreground">{formatWhen(q.data.conversation.startedAt, ws.timezone)}</span>
              <span className="figures text-sm text-muted-foreground">{formatDuration(q.data.conversation.durationSeconds)} · {q.data.conversation.billedMinutes} min billed</span>
            </div>
            <p className="text-sm">
              <strong>{q.data.contact?.name ?? 'Caller'}</strong> {q.data.contact?.phone ? <span className="figures text-muted-foreground">{q.data.contact.phone}</span> : null}
            </p>
            {q.data.conversation.channel === 'voice' ? <CallReview review={q.data.review} /> : null}
            <div className="space-y-2">
              {q.data.transcript.map((t: any) =>
                t.sender === 'tool' ? (
                  <p key={t.id} className="text-center text-xs text-muted-foreground">
                    used {t.content}
                  </p>
                ) : (
                  <div key={t.id} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${t.sender === 'customer' ? 'ml-auto bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    {t.content}
                  </div>
                ),
              )}
              {q.data.transcript.length === 0 ? <p className="text-sm text-muted-foreground">No transcript.</p> : null}
            </div>
            {q.data.appointments.length ? (
              <div>
                <h3 className="mb-2 text-sm font-semibold">Booked on this call</h3>
                {q.data.appointments.map((a: any) => (
                  <p key={a.id} className="text-sm">
                    {a.serviceName} · {formatWhen(a.startsAt, ws.timezone)}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

const FLAG_LABELS: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' }> = {
  booked: { label: 'Booked', variant: 'success' },
  great_call: { label: 'Great call', variant: 'success' },
  missed_booking_opportunity: { label: 'Missed a booking', variant: 'warning' },
  wrong_or_unsure_info: { label: 'Wrong or unsure info', variant: 'destructive' },
  customer_frustrated: { label: 'Caller frustrated', variant: 'destructive' },
  should_have_escalated: { label: 'Should have handed off', variant: 'warning' },
  language_issue: { label: 'Language issue', variant: 'warning' },
  long_silence_or_confusion: { label: 'Silence or confusion', variant: 'warning' },
};

type Review = { score: number | null; flags: string[]; summary: string | null; needsReview: boolean; recordingUrl: string | null } | null;

/** Recording player and the AI quality review; the upgrade prompt on plans without analytics. */
function CallReview({ review }: { review: Review }) {
  const ws = useWorkspace();
  const [audioFailed, setAudioFailed] = useState(false);
  if (!review) {
    const up = ws.locked.find((l) => l.feature === 'analytics');
    return up ? <UpgradePrompt upgrade={{ ...up, label: 'Call recordings and quality review' }} compact /> : null;
  }
  return (
    <div className="space-y-3 rounded-xl border p-4">
      {review.recordingUrl && !audioFailed ? (
        <audio controls preload="none" src={apiUrl(review.recordingUrl)} onError={() => setAudioFailed(true)} className="w-full">
          <track kind="captions" />
        </audio>
      ) : (
        <p className="text-sm text-muted-foreground">{review.recordingUrl ? 'The recording is not available.' : 'No recording for this call.'}</p>
      )}
      {review.score !== null ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <ScoreBadge score={review.score} needsReview={review.needsReview} />
            {review.flags.map((f) => (
              <Badge key={f} variant={FLAG_LABELS[f]?.variant ?? 'warning'}>
                {FLAG_LABELS[f]?.label ?? f}
              </Badge>
            ))}
          </div>
          {review.summary ? <p className="text-sm">{review.summary}</p> : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Not scored yet. Very short calls are not scored.</p>
      )}
    </div>
  );
}

const STATUS_LABEL: Record<string, { label: string; variant: 'success' | 'warning' | 'default' | 'destructive' | 'info' }> = {
  booked: { label: 'Booked', variant: 'info' },
  pending_confirmation: { label: 'Needs confirmation', variant: 'warning' },
  pending_deposit: { label: 'Held for deposit', variant: 'warning' },
  completed: { label: 'Completed', variant: 'success' },
  no_show: { label: 'No-show', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'default' },
};

export function BookingsPage() {
  const ws = useWorkspace();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const q = useQuery({ queryKey: ['bookings'], queryFn: () => get('/api/bookings') });
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get('/api/settings') });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customerName: '', customerPhone: '', service: '', date: '', time: '' });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) =>
      // Confirm / decline answer whatever is waiting (a booking or a reschedule request).
      v.status === 'confirm' || v.status === 'decline' ? post<{ message?: string }>(`/api/bookings/${v.id}/review`, { action: v.status }) : patch<{ message?: string }>(`/api/bookings/${v.id}`, { status: v.status }),
    onSuccess: (r) => {
      // Confirm / decline say whether the customer was texted or needs a call.
      if (r.message) success('Updated', r.message);
      return qc.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (e) => error('Could not update', errorMessage(e)),
  });
  const create = useMutation({
    mutationFn: () => post('/api/bookings', { ...form, customerPhone: form.customerPhone || undefined }),
    onSuccess: () => {
      success('Booked');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['bookings'] });
    },
    onError: (e) => error('Could not book', errorMessage(e)),
  });
  return (
    <div className="space-y-6">
      <PageHeader title="Bookings" description="Appointments booked by your AI receptionist, website chat and your team." actions={<Button onClick={() => setOpen(true)}><CalendarPlus /> New booking</Button>} />
      {q.isLoading ? <LoadingState /> : null}
      <Card className="divide-y">
        {q.data?.bookings.length === 0 ? <EmptyState title="No bookings in this range" /> : null}
        {q.data?.bookings.map((b: any) => (
          <div key={b.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
            <span className="figures w-44 text-sm">{formatWhen(b.startsAt, ws.timezone)}</span>
            <span className="min-w-0 flex-1 truncate text-sm">
              <strong>{b.serviceName}</strong> · {b.contactName ?? 'Customer'} {b.source === 'simulator' ? <Badge variant="outline">Test</Badge> : null}
            </span>
            <Badge variant={STATUS_LABEL[b.status]?.variant ?? 'default'}>{STATUS_LABEL[b.status]?.label ?? b.status}</Badge>
            <DepositBadge cents={b.depositCents} status={b.depositStatus} />
            {b.proposedStartsAt ? <Badge variant="warning">Wants to move to {formatWhen(b.proposedStartsAt, ws.timezone)}</Badge> : null}
            {b.status === 'pending_confirmation' || b.proposedStartsAt ? (
              <span className="flex gap-2">
                <Button size="sm" onClick={() => setStatus.mutate({ id: b.id, status: 'confirm' })} loading={setStatus.isPending && setStatus.variables?.id === b.id && setStatus.variables.status === 'confirm'}>
                  {b.proposedStartsAt ? 'Move it' : 'Confirm'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: b.id, status: 'decline' })} loading={setStatus.isPending && setStatus.variables?.id === b.id && setStatus.variables.status === 'decline'}>
                  {b.proposedStartsAt ? 'Keep current time' : 'Decline'}
                </Button>
              </span>
            ) : null}
            <Select
              aria-label="Change status"
              className="w-40"
              value=""
              onChange={(e) => e.target.value && setStatus.mutate({ id: b.id, status: e.target.value })}
              options={[
                { value: '', label: 'Set status...' },
                { value: 'completed', label: 'Completed' },
                { value: 'no_show', label: 'No-show' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </div>
        ))}
      </Card>
      {hasFeature(ws, 'reminders') ? <WaitlistCard /> : null}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent side="right" title="New booking">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="bk-name">Customer name</Label>
              <Input id="bk-name" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bk-phone">Phone</Label>
              <Input id="bk-phone" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bk-service">Service</Label>
              <Select id="bk-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Choose a service" options={(settings.data?.services ?? []).map((s: any) => ({ value: s.id, label: `${s.name} (${s.duration_minutes} min)` }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="bk-date">Date</Label>
                <Input id="bk-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bk-time">Time</Label>
                <Input id="bk-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required />
              </div>
            </div>
            <Button type="submit" loading={create.isPending} className="w-full">
              Book
            </Button>
          </form>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

export function InboxPage() {
  const ws = useWorkspace();
  const qc = useQueryClient();
  const { search } = useRouter();
  const messages = useQuery({ queryKey: ['messages'], queryFn: () => get('/api/messages') });
  const escalations = useQuery({ queryKey: ['escalations'], queryFn: () => get('/api/escalations') });
  const done = useMutation({ mutationFn: (id: string) => patch(`/api/messages/${id}`, { status: 'done' }), onSuccess: () => qc.invalidateQueries() });
  const resolve = useMutation({ mutationFn: (id: string) => post(`/api/escalations/${id}/resolve`), onSuccess: () => qc.invalidateQueries() });
  return (
    <div className="space-y-6">
      <PageHeader title="Inbox" description="Messages your receptionist took and callers who asked for a person." />
      <Tabs defaultValue={search.get('tab') ?? 'messages'}>
        <TabsList>
          <TabsTrigger value="messages">Messages</TabsTrigger>
          <TabsTrigger value="escalations">Needs a person</TabsTrigger>
        </TabsList>
        <TabsContent value="messages">
          <Card className="divide-y">
            {messages.data?.messages.length === 0 ? <EmptyState title="No messages" /> : null}
            {messages.data?.messages.map((m: any) => (
              <div key={m.id} className={`flex flex-wrap items-start gap-3 px-5 py-4 ${m.status === 'done' ? 'opacity-60' : ''}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {m.callerName ?? 'Caller'} <span className="figures font-normal text-muted-foreground">{m.callerPhone}</span>
                  </p>
                  <p className="mt-1 text-sm">{m.body}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{formatWhen(m.createdAt, ws.timezone)}</p>
                </div>
                {m.status === 'new' ? (
                  <Button size="sm" variant="outline" onClick={() => done.mutate(m.id)}>
                    <CheckCircle2 /> Done
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>
        </TabsContent>
        <TabsContent value="escalations">
          <Card className="divide-y">
            {escalations.data?.escalations.length === 0 ? <EmptyState title="Nobody waiting" /> : null}
            {escalations.data?.escalations.map((e: any) => (
              <div key={e.id} className={`flex flex-wrap items-center gap-3 px-5 py-4 ${e.resolvedAt ? 'opacity-60' : ''}`}>
                <Badge variant={e.reason === 'emergency' ? 'destructive' : 'warning'}>{e.reason.replace(/_/g, ' ')}</Badge>
                <span className="flex-1 text-sm">
                  {e.contactName ?? 'Caller'} <span className="figures text-muted-foreground">{e.contactPhone}</span> {e.transferred ? <Badge variant="info">Transferred live</Badge> : null}
                </span>
                {e.conversationId ? <Link className="text-sm underline" to={`/calls/${e.conversationId}`}>Transcript</Link> : null}
                {!e.resolvedAt ? (
                  <Button size="sm" variant="outline" onClick={() => resolve.mutate(e.id)}>
                    Resolve
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
